import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { isBillingUiEnabled, postWithAiBilling } from '../billing/billingApi';
import BillingBalance from '../billing/BillingBalance';
import { useNavigate } from 'react-router-dom';
import { AiOutlineClose } from 'react-icons/ai';
import { FiDownload, FiLoader, FiPlus } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { toJpeg, toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import katex from 'katex';
import { compile } from 'mathjs';
import { Coordinates, Mafs, Plot, Point, Text as MafsText } from 'mafs';
import 'katex/dist/katex.min.css';
import 'mafs/core.css';
import './AiStudy.scss';
import uploadIcon from '../images/page/upload.png';
import removeIcon from '../images/page/remove.png';
import addImageIcon from '../images/page/plus.png';

const TARGET_IMAGE_BYTES = 360 * 1024;
const HARD_IMAGE_BYTES_LIMIT = 700 * 1024;
const MIN_IMAGE_SIDE = 320;
const AI_STUDY_S3_FOLDER = 'gemini_fileUpload';
const AI_STUDY_S3_BUCKET = 'public-kuesb';
const GRADE_STORAGE_KEY = 'ai-study-grade-level';
const ANSWER_MODE_STORAGE_KEY = 'ai-study-answer-mode';
const INPUT_DRAFT_STORAGE_KEY = 'ai-study-input-draft';
const DEFAULT_VIEWPORT = { x: [-5, 5], y: [-5, 5] };
const FIXED_RENDER_VIEWPORT = { x: [-5, 5], y: [-5, 5] };
const GRAPH_CAPTURE_SIZE = 640;
const SAFE_EXPR_RE = /^[0-9xX+\-*/^().,\s_a-zA-Z]+$/;
const MIN_CROP_SPAN_PERCENT = 10;

function clampPercent(value, min = 0, max = 100) {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, value));
}

function isValidGradeLevel(value) {
  return value === 'all' || value === 'elementary' || value === 'middle' || value === 'high' || value === 'college';
}

function isValidAnswerMode(value) {
  return value === 'fast' || value === 'normal' || value === 'precise';
}

function getBase64Bytes(dataUrl) {
  if (typeof dataUrl !== 'string') {
    return 0;
  }

  const base64 = dataUrl.split(',')[1] || '';
  if (!base64) {
    return 0;
  }

  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function sanitizeSvgMarkup(svgMarkup) {
  if (typeof window === 'undefined' || typeof svgMarkup !== 'string') {
    return '';
  }

  const parser = new window.DOMParser();
  const parsed = parser.parseFromString(svgMarkup, 'image/svg+xml');
  const parserError = parsed.querySelector('parsererror');

  if (parserError) {
    return '';
  }

  const svg = parsed.querySelector('svg');

  if (!svg) {
    return '';
  }

  parsed.querySelectorAll('script, foreignObject').forEach((node) => node.remove());

  parsed.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const attrName = attribute.name.toLowerCase();
      const attrValue = attribute.value.toLowerCase();

      if (attrName.startsWith('on')) {
        node.removeAttribute(attribute.name);
        return;
      }

      if ((attrName === 'href' || attrName === 'xlink:href') && attrValue.startsWith('javascript:')) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return svg.outerHTML;
}

function SvgBlock({ code }) {
  const safeSvg = useMemo(() => sanitizeSvgMarkup(code), [code]);

  if (!safeSvg) {
    return (
      <pre>
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div className="ai-diagram-block">
      <div className="ai-diagram-svg" dangerouslySetInnerHTML={{ __html: safeSvg }} />
    </div>
  );
}

function isFiniteNumber(value) {
  return Number.isFinite(value);
}

function normalizePair(pair) {
  if (!Array.isArray(pair) || pair.length < 2) {
    return null;
  }

  const x = Number(pair[0]);
  const y = Number(pair[1]);

  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    return null;
  }

  return [x, y];
}

function normalizeRange(range, fallback) {
  if (!Array.isArray(range) || range.length < 2) {
    return fallback;
  }

  const min = Number(range[0]);
  const max = Number(range[1]);

  if (!isFiniteNumber(min) || !isFiniteNumber(max) || min >= max) {
    return fallback;
  }

  return [min, max];
}

function buildFunctionEvaluator(expr) {
  if (typeof expr !== 'string') {
    return null;
  }

  const trimmed = expr.trim();

  if (!trimmed || !SAFE_EXPR_RE.test(trimmed)) {
    return null;
  }

  try {
    const compiled = compile(trimmed);

    return (x) => {
      const value = compiled.evaluate({ x, pi: Math.PI, e: Math.E });
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : NaN;
    };
  } catch (error) {
    return null;
  }
}

function normalizeGraphText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\$+/g, '')
    .replace(/\\pi/g, 'pi')
    .replace(/\\cdot/g, '*')
    .replace(/\\times/g, 'x')
    .replace(/\\left|\\right/g, '')
    .replace(/\\,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function looksLikeMathLabel(value) {
  if (typeof value !== 'string') {
    return false;
  }

  return /\$|\\[a-zA-Z]+|\^|_|=|\bsin\b|\bcos\b|\btan\b|\bpi\b/.test(value);
}

function toKatexHtml(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const raw = value.trim();
  if (!raw) {
    return '';
  }

  const latex = raw.replace(/^\$+|\$+$/g, '').trim();

  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false,
      output: 'html',
      strict: 'ignore',
    });
  } catch (error) {
    return '';
  }
}

function toOverlayPosition(coord, viewport) {
  if (!Array.isArray(coord) || coord.length < 2 || !viewport) {
    return null;
  }

  const [x, y] = coord;
  const [xMin, xMax] = viewport.x;
  const [yMin, yMax] = viewport.y;

  if (xMax === xMin || yMax === yMin) {
    return null;
  }

  const left = ((x - xMin) / (xMax - xMin)) * 100;
  const top = (1 - (y - yMin) / (yMax - yMin)) * 100;

  return {
    left: `${Math.max(0, Math.min(100, left))}%`,
    top: `${Math.max(0, Math.min(100, top))}%`,
  };
}

function buildAxisArrowSegments(viewport) {
  if (!viewport || !Array.isArray(viewport.x) || !Array.isArray(viewport.y)) {
    return [];
  }

  const [xMin, xMax] = viewport.x;
  const [yMin, yMax] = viewport.y;
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;

  if (!Number.isFinite(xSpan) || !Number.isFinite(ySpan) || xSpan <= 0 || ySpan <= 0) {
    return [];
  }

  const segments = [];
  const xAxisVisible = yMin <= 0 && yMax >= 0;
  const yAxisVisible = xMin <= 0 && xMax >= 0;

  if (xAxisVisible) {
    const xArrowLength = Math.max(xSpan * 0.018, 0.14);
    const xArrowHalfHeight = Math.max(ySpan * 0.014, 0.1);
    const tip = [xMax, 0];
    const baseX = xMax - xArrowLength;

    segments.push({ from: [baseX, xArrowHalfHeight], to: tip });
    segments.push({ from: [baseX, -xArrowHalfHeight], to: tip });
  }

  if (yAxisVisible) {
    const yArrowLength = Math.max(ySpan * 0.018, 0.14);
    const yArrowHalfWidth = Math.max(xSpan * 0.014, 0.1);
    const tip = [0, yMax];
    const baseY = yMax - yArrowLength;

    segments.push({ from: [yArrowHalfWidth, baseY], to: tip });
    segments.push({ from: [-yArrowHalfWidth, baseY], to: tip });
  }

  return segments;
}

function svgElementToDataUrl(svgElement) {
  if (!svgElement) {
    return '';
  }

  try {
    const cloned = svgElement.cloneNode(true);
    const width = svgElement.clientWidth || 640;
    const height = svgElement.clientHeight || 640;

    if (!cloned.getAttribute('xmlns')) {
      cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }

    cloned.setAttribute('width', String(width));
    cloned.setAttribute('height', String(height));

    const serialized = new window.XMLSerializer().serializeToString(cloned);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;
  } catch (error) {
    return '';
  }
}

function parseGraphSpec(graphText) {
  if (typeof graphText !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(graphText);

    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.objects)) {
      return null;
    }

    const viewportX = normalizeRange(parsed.viewport?.x, DEFAULT_VIEWPORT.x);
    const viewportY = normalizeRange(parsed.viewport?.y, DEFAULT_VIEWPORT.y);
    const objects = parsed.objects
      .slice(0, 40)
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const type = typeof item.type === 'string' ? item.type.toLowerCase() : '';

        if (type === 'function') {
          const fn = buildFunctionEvaluator(item.expr);
          if (!fn) {
            return null;
          }

          return {
            type,
            fn,
            color: typeof item.color === 'string' ? item.color : '#0f766e',
            domain: normalizeRange(item.domain, viewportX),
          };
        }

        if (type === 'point') {
          const coord = normalizePair(item.coord);
          if (!coord) {
            return null;
          }

          const rawLabel = typeof item.label === 'string' ? item.label : '';
          const label = normalizeGraphText(item.label);

          return {
            type,
            coord,
            label,
            rawLabel,
            color: typeof item.color === 'string' ? item.color : '#dc2626',
          };
        }

        if (type === 'line') {
          const from = normalizePair(item.from);
          const to = normalizePair(item.to);

          if (!from || !to) {
            return null;
          }

          return {
            type,
            from,
            to,
            color: typeof item.color === 'string' ? item.color : '#2563eb',
          };
        }

        if (type === 'text') {
          const at = normalizePair(item.at);
          const rawText = typeof item.text === 'string' ? item.text : '';
          const text = normalizeGraphText(item.text);
          if (!at || !text) {
            return null;
          }

          return {
            type,
            at,
            text,
            rawText,
            color: typeof item.color === 'string' ? item.color : '#111827',
          };
        }

        return null;
      })
      .filter(Boolean);

    if (objects.length === 0) {
      return null;
    }

    return {
      viewport: {
        x: viewportX,
        y: viewportY,
      },
      objects,
    };
  } catch (error) {
    return null;
  }
}

function GraphBlock({ code }) {
  const spec = useMemo(() => parseGraphSpec(code), [code]);
  const captureRef = useRef(null);
  const [graphImageUrl, setGraphImageUrl] = useState('');
  const [captureVersion, setCaptureVersion] = useState(0);
  const renderViewport = FIXED_RENDER_VIEWPORT;
  const axisArrows = useMemo(() => {
    if (!spec) {
      return [];
    }

    return buildAxisArrowSegments(renderViewport);
  }, [spec, renderViewport]);
  const latexOverlays = useMemo(() => {
    if (!spec) {
      return [];
    }

    const overlays = [];

    spec.objects.forEach((item, index) => {
      if (item.type === 'point' && item.rawLabel && looksLikeMathLabel(item.rawLabel)) {
        const position = toOverlayPosition(item.coord, renderViewport);
        const html = toKatexHtml(item.rawLabel);

        if (position && html) {
          overlays.push({
            key: `p-${index}`,
            position,
            html,
          });
        }
      }

      if (item.type === 'text' && item.rawText && looksLikeMathLabel(item.rawText)) {
        const position = toOverlayPosition(item.at, renderViewport);
        const html = toKatexHtml(item.rawText);

        if (position && html) {
          overlays.push({
            key: `t-${index}`,
            position,
            html,
          });
        }
      }
    });

    return overlays;
  }, [spec, renderViewport]);

  useEffect(() => {
    setGraphImageUrl('');
  }, [code]);

  useEffect(() => {
    if (!spec || !captureRef.current) {
      return undefined;
    }

    let rafId = 0;
    const requestRecapture = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setCaptureVersion((prev) => prev + 1);
      });
    };

    const resizeObserver = new window.ResizeObserver(() => {
      requestRecapture();
    });

    resizeObserver.observe(captureRef.current);
    window.addEventListener('resize', requestRecapture);

    // dev 환경에서 SCSS HMR로 <style> 태그가 갱신되는 경우 자동 재캡처한다.
    const headObserver = new window.MutationObserver(() => {
      requestRecapture();
    });

    if (document.head) {
      headObserver.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      resizeObserver.disconnect();
      headObserver.disconnect();
      window.removeEventListener('resize', requestRecapture);
    };
  }, [spec]);

  useEffect(() => {
    if (!spec || !captureRef.current) {
      return undefined;
    }

    let cancelled = false;

    const captureAsImage = async () => {
      try {
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });

        if (cancelled || !captureRef.current) {
          return;
        }

        const dataUrl = await toPng(captureRef.current, {
          cacheBust: true,
          pixelRatio: 2,
          backgroundColor: '#f3f4f6',
        });

        if (!cancelled) {
          setGraphImageUrl(dataUrl);
        }
      } catch (error) {
        if (!cancelled) {
          const svg = captureRef.current?.querySelector('svg');
          const fallbackUrl = svgElementToDataUrl(svg);
          setGraphImageUrl(fallbackUrl || '');
        }
      }
    };

    captureAsImage();

    return () => {
      cancelled = true;
    };
  }, [spec, code, latexOverlays.length, captureVersion]);

  if (!spec) {
    return (
      <pre>
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div className="ai-diagram-block">
      <div className="ai-diagram-capture-stage" aria-hidden="true">
        <div className="ai-diagram-mafs" ref={captureRef}>
          <Mafs
            width={GRAPH_CAPTURE_SIZE}
            height={GRAPH_CAPTURE_SIZE}
            viewBox={renderViewport}
            preserveAspectRatio
          >
            <Coordinates.Cartesian
              subdivisions={false}
              xAxis={{ axis: true, lines: 1 }}
              yAxis={{ axis: true, lines: 1 }}
            />
            <Plot.Parametric
              xy={(t) => [
                renderViewport.x[0] + (renderViewport.x[1] - renderViewport.x[0]) * t,
                0,
              ]}
              t={[0, 1]}
              color="#111827"
              weight={2}
            />
            <Plot.Parametric
              xy={(t) => [
                0,
                renderViewport.y[0] + (renderViewport.y[1] - renderViewport.y[0]) * t,
              ]}
              t={[0, 1]}
              color="#111827"
              weight={2}
            />
            {axisArrows.map((segment, index) => (
              <Plot.Parametric
                key={`axis-arrow-${index}`}
                xy={(t) => [
                  segment.from[0] + (segment.to[0] - segment.from[0]) * t,
                  segment.from[1] + (segment.to[1] - segment.from[1]) * t,
                ]}
                t={[0, 1]}
                color="#111827"
                weight={2}
              />
            ))}
            {spec.objects.map((item, index) => {
              if (item.type === 'function') {
                return <Plot.OfX key={`fn-${index}`} y={item.fn} domain={item.domain} color={item.color} />;
              }

              if (item.type === 'point') {
                return (
                  <React.Fragment key={`point-${index}`}>
                    <Point x={item.coord[0]} y={item.coord[1]} color={item.color} />
                    {item.label && !looksLikeMathLabel(item.rawLabel) && (
                      <MafsText x={item.coord[0]} y={item.coord[1]} attach="ne" color="#111827">
                        {item.label}
                      </MafsText>
                    )}
                  </React.Fragment>
                );
              }

              if (item.type === 'line') {
                return (
                  <Plot.Parametric
                    key={`line-${index}`}
                    xy={(t) => [
                      item.from[0] + (item.to[0] - item.from[0]) * t,
                      item.from[1] + (item.to[1] - item.from[1]) * t,
                    ]}
                    t={[0, 1]}
                    color={item.color}
                  />
                );
              }

              if (item.type === 'text') {
                if (looksLikeMathLabel(item.rawText)) {
                  return null;
                }

                return (
                  <MafsText key={`text-${index}`} x={item.at[0]} y={item.at[1]} color="#111827">
                    {item.text}
                  </MafsText>
                );
              }

              return null;
            })}
          </Mafs>
          {latexOverlays.length > 0 && (
            <div className="ai-diagram-latex-layer" aria-hidden="true">
              {latexOverlays.map((overlay) => (
                <span
                  key={overlay.key}
                  className="ai-diagram-latex-label"
                  style={overlay.position}
                  dangerouslySetInnerHTML={{ __html: overlay.html }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {graphImageUrl ? (
        <div className="ai-diagram-image-wrap">
          <img src={graphImageUrl} alt="좌표 그래프" className="ai-diagram-image" />
        </div>
      ) : (
        <div className="ai-diagram-image-pending">이미지 생성 중</div>
      )}
    </div>
  );
}

function MarkdownCode({ inline, className, children, ...props }) {
  const codeText = String(children).replace(/\n$/, '');
  const language = className?.replace('language-', '').toLowerCase();

  if (!inline && language === 'graph') {
    return <GraphBlock code={codeText} />;
  }

  if (!inline && language === 'svg') {
    return <SvgBlock code={codeText} />;
  }

  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
}

function parseDataUrl(dataUrl) {
  if (typeof dataUrl !== 'string') {
    return null;
  }

  const matched = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!matched) {
    return null;
  }

  return {
    mimeType: matched[1],
    dataBase64: matched[2],
    bytes: getBase64Bytes(dataUrl),
  };
}

function dataUrlToBlob(dataUrl) {
  const parsed = parseDataUrl(dataUrl);

  if (!parsed) {
    return null;
  }

  const binary = window.atob(parsed.dataBase64);
  const length = binary.length;
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: parsed.mimeType });
}

function getImageExtension(mimeType) {
  if (typeof mimeType !== 'string') {
    return 'jpg';
  }

  if (mimeType.includes('png')) {
    return 'png';
  }

  if (mimeType.includes('webp')) {
    return 'webp';
  }

  if (mimeType.includes('gif')) {
    return 'gif';
  }

  return 'jpg';
}

function buildUploadFileName(mimeType) {
  const extension = getImageExtension(mimeType);
  const randomPart = Math.random().toString(36).slice(2, 8);
  return `add-image-${Date.now()}-${randomPart}.${extension}`;
}

async function uploadImageBlobToS3(serverUrl, imageBlob, mimeType) {
  const fileName = buildUploadFileName(mimeType);

  const uploadUrlResponse = await axios.post(`${serverUrl}/generate-upload-url`, {
    fileName,
    fileType: mimeType || 'image/jpeg',
    folder: AI_STUDY_S3_FOLDER,
    s3Bucket: AI_STUDY_S3_BUCKET,
    sec: 900,
  });

  const presignedUrl = uploadUrlResponse.data?.url;
  const s3Key = uploadUrlResponse.data?.key;
  const s3Bucket = uploadUrlResponse.data?.bucket || AI_STUDY_S3_BUCKET;

  if (!presignedUrl || !s3Key) {
    throw new Error('S3 업로드 URL 생성에 실패했습니다.');
  }

  let putResponse;

  try {
    putResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: imageBlob,
    });
  } catch (networkError) {
    throw new Error('S3 업로드 요청이 브라우저에서 차단되었습니다. 버킷 CORS 설정을 확인해 주세요.');
  }

  if (!putResponse.ok) {
    const errorText = await putResponse.text().catch(() => '');
    const compactError = errorText.replace(/\s+/g, ' ').trim().slice(0, 240);
    const detailSuffix = compactError ? `: ${compactError}` : '';
    throw new Error(`S3 업로드 실패 (status: ${putResponse.status})${detailSuffix}`);
  }

  return { s3Key, s3Bucket };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        reject(new Error('이미지 읽기에 실패했습니다. 다시 시도해 주세요.'));
        return;
      }

      resolve(result);
    };

    reader.onerror = () => reject(new Error('이미지 읽기에 실패했습니다. 다시 시도해 주세요.'));
    reader.readAsDataURL(file);
  });
}

function loadImageFromDataUrl(sourceDataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('이미지 형식을 처리할 수 없습니다.'));
    image.src = sourceDataUrl;
  });
}

async function cropImageDataUrlByPercentRect(sourceDataUrl, cropRectPercent) {
  const image = await loadImageFromDataUrl(sourceDataUrl);
  const safeTop = clampPercent(Number(cropRectPercent?.top), 0, 100);
  const safeBottom = clampPercent(Number(cropRectPercent?.bottom), 0, 100);
  const safeLeft = clampPercent(Number(cropRectPercent?.left), 0, 100);
  const safeRight = clampPercent(Number(cropRectPercent?.right), 0, 100);
  const cropTop = Math.round((image.height * safeTop) / 100);
  const cropBottom = Math.round((image.height * safeBottom) / 100);
  const cropLeft = Math.round((image.width * safeLeft) / 100);
  const cropRight = Math.round((image.width * safeRight) / 100);
  const rawCropHeight = image.height - cropTop - cropBottom;
  const rawCropWidth = image.width - cropLeft - cropRight;
  const cropHeight = Math.max(40, rawCropHeight);
  const cropWidth = Math.max(40, rawCropWidth);
  const maxStartY = Math.max(0, image.height - cropHeight);
  const maxStartX = Math.max(0, image.width - cropWidth);
  const sourceY = Math.max(0, Math.min(maxStartY, cropTop));
  const sourceX = Math.max(0, Math.min(maxStartX, cropLeft));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('이미지 처리에 실패했습니다.');
  }

  canvas.width = cropWidth;
  canvas.height = cropHeight;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(
    image,
    sourceX,
    sourceY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  return canvas.toDataURL('image/jpeg', 0.95);
}

async function resizeImageDataUrl(sourceDataUrl) {
  const image = await loadImageFromDataUrl(sourceDataUrl);
  const sourcePixels = image.width * image.height;
  const isLargeCapture = sourcePixels >= 1400000;
  const isVeryLargeCapture = sourcePixels >= 2200000;
  const maxSize = isVeryLargeCapture ? 680 : isLargeCapture ? 760 : 900;
  const targetBytes = isVeryLargeCapture ? 240 * 1024 : isLargeCapture ? 300 * 1024 : TARGET_IMAGE_BYTES;
  const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
  let width = Math.max(1, Math.round(image.width * scale));
  let height = Math.max(1, Math.round(image.height * scale));
  let quality = 0.65;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('이미지 처리에 실패했습니다.');
  }

  const renderToDataUrl = (format, formatQuality) => {
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    if (typeof formatQuality === 'number') {
      return canvas.toDataURL(format, formatQuality);
    }

    return canvas.toDataURL(format);
  };

  let dataUrl = renderToDataUrl('image/jpeg', quality);
  let byteSize = getBase64Bytes(dataUrl);

  // 배포 환경에서 프록시 업로드 제한(예: 1MB)으로 실패하는 경우를 줄이기 위해 반복 압축한다.
  for (let i = 0; i < 7 && byteSize > targetBytes; i += 1) {
    quality = Math.max(0.35, quality - 0.07);

    if (byteSize > targetBytes) {
      const shrinkRatio = Math.sqrt(targetBytes / byteSize);
      const safeRatio = Number.isFinite(shrinkRatio) ? Math.max(0.65, Math.min(0.9, shrinkRatio)) : 0.82;
      width = Math.max(MIN_IMAGE_SIDE, Math.round(width * safeRatio));
      height = Math.max(MIN_IMAGE_SIDE, Math.round(height * safeRatio));
    }

    dataUrl = renderToDataUrl('image/jpeg', quality);
    byteSize = getBase64Bytes(dataUrl);
  }

  // 스크린샷은 PNG가 더 작게 나오는 경우가 있어, 마지막에 더 작은 포맷을 선택한다.
  const pngDataUrl = renderToDataUrl('image/png');
  const pngByteSize = getBase64Bytes(pngDataUrl);

  if (pngByteSize > 0 && pngByteSize < byteSize) {
    dataUrl = pngDataUrl;
    byteSize = pngByteSize;
  }

  if (byteSize > HARD_IMAGE_BYTES_LIMIT) {
    throw new Error('이미지 용량이 커서 전송할 수 없습니다. 더 작은 이미지를 선택해 주세요.');
  }

  return dataUrl;
}

function resizeImageFile(imageFile) {
  return readFileAsDataUrl(imageFile).then((sourceDataUrl) => resizeImageDataUrl(sourceDataUrl));
}

function formatLocalDate(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function loadPdfImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('PDF 이미지 변환에 실패했습니다.'));
    image.src = dataUrl;
  });
}

function findPdfSliceHeight(image, sourceY, proposedHeight) {
  const remainingHeight = image.naturalHeight - sourceY;
  if (remainingHeight <= proposedHeight) {
    return remainingHeight;
  }

  const exportScale = Math.max(1, image.naturalWidth / 800);
  const searchWindow = Math.min(proposedHeight, Math.round(180 * exportScale));
  const searchStart = sourceY + proposedHeight - searchWindow;
  const analyzer = document.createElement('canvas');
  analyzer.width = image.naturalWidth;
  analyzer.height = searchWindow;
  const context = analyzer.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return proposedHeight;
  }

  context.drawImage(
    image,
    0,
    searchStart,
    image.naturalWidth,
    searchWindow,
    0,
    0,
    image.naturalWidth,
    searchWindow,
  );

  const pixels = context.getImageData(0, 0, analyzer.width, analyzer.height).data;
  const sampleStep = Math.max(2, Math.round(3 * exportScale));
  const maxInkSamples = Math.max(2, Math.floor((analyzer.width / sampleStep) * 0.008));
  const minimumBlankRows = Math.max(6, Math.round(8 * exportScale));
  let blankRows = 0;

  for (let y = analyzer.height - 1; y >= 0; y -= 1) {
    let inkSamples = 0;
    const rowOffset = y * analyzer.width * 4;

    for (let x = 0; x < analyzer.width; x += sampleStep) {
      const pixelOffset = rowOffset + (x * 4);
      if (
        pixels[pixelOffset] < 244
        || pixels[pixelOffset + 1] < 244
        || pixels[pixelOffset + 2] < 244
      ) {
        inkSamples += 1;
        if (inkSamples > maxInkSamples) {
          break;
        }
      }
    }

    if (inkSamples <= maxInkSamples) {
      blankRows += 1;
      if (blankRows >= minimumBlankRows) {
        return (searchStart - sourceY) + y + Math.floor(minimumBlankRows / 2);
      }
    } else {
      blankRows = 0;
    }
  }

  return proposedHeight;
}

function AiStudy() {
  const navigate = useNavigate();
  const defaultServerUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8002' : '';
  const serverUrl = (process.env.REACT_APP_SERVER_URL || defaultServerUrl).replace(/\/$/, '');

  const [messages, setMessages] = useState([
    {
      id: 'init-1',
      role: 'assistant',
      content:
        '안녕하세요. 질문을 보내면 단계별로 풀이해 드립니다.',
      createdAt: Date.now(),
    },
  ]);
  const [input, setInput] = useState('');
  const [pastedImage, setPastedImage] = useState(null);
  const [gradeLevel, setGradeLevel] = useState('all');
  const [answerMode, setAnswerMode] = useState('normal');
  const [activeHelp, setActiveHelp] = useState('');
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropSourceDataUrl, setCropSourceDataUrl] = useState('');
  const [cropTopPercent, setCropTopPercent] = useState(0);
  const [cropBottomPercent, setCropBottomPercent] = useState(0);
  const [cropLeftPercent, setCropLeftPercent] = useState(0);
  const [cropRightPercent, setCropRightPercent] = useState(0);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [error, setError] = useState('');
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  const listRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const imagePickerRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const cropDragStateRef = useRef(null);
  const gradeHelpRef = useRef(null);
  const answerModeHelpRef = useRef(null);
  const isInputDraftHydratedRef = useRef(false);
  const requestInFlightRef = useRef(false);

  const closeCropModal = () => {
    if (isApplyingCrop) {
      return;
    }

    setIsCropModalOpen(false);
    setCropSourceDataUrl('');
    setCropTopPercent(0);
    setCropBottomPercent(0);
    setCropLeftPercent(0);
    setCropRightPercent(0);
  };

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(GRADE_STORAGE_KEY);
      if (saved && isValidGradeLevel(saved)) {
        setGradeLevel(saved);
      }
    } catch (error) {
      // Ignore localStorage access errors.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(GRADE_STORAGE_KEY, gradeLevel);
    } catch (error) {
      // Ignore localStorage access errors.
    }
  }, [gradeLevel]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(ANSWER_MODE_STORAGE_KEY);
      if (saved && isValidAnswerMode(saved)) {
        setAnswerMode(saved);
      }
    } catch (error) {
      // Ignore localStorage access errors.
    }
  }, []);

  useEffect(() => {
    try {
      const savedDraft = window.localStorage.getItem(INPUT_DRAFT_STORAGE_KEY);
      if (typeof savedDraft === 'string' && savedDraft.length > 0) {
        setInput(savedDraft);
      }
    } catch (error) {
      // Ignore localStorage access errors.
    } finally {
      isInputDraftHydratedRef.current = true;
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(ANSWER_MODE_STORAGE_KEY, answerMode);
    } catch (error) {
      // Ignore localStorage access errors.
    }
  }, [answerMode]);

  useEffect(() => {
    if (!isInputDraftHydratedRef.current) {
      return;
    }

    try {
      if (input && input.trim().length > 0) {
        window.localStorage.setItem(INPUT_DRAFT_STORAGE_KEY, input);
      } else {
        window.localStorage.removeItem(INPUT_DRAFT_STORAGE_KEY);
      }
    } catch (error) {
      // Ignore localStorage access errors.
    }
  }, [input]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      const isHelpTarget = gradeHelpRef.current?.contains(target) || answerModeHelpRef.current?.contains(target);
      const isImagePickerTarget = imagePickerRef.current?.contains(target);

      if (!isHelpTarget) {
        setActiveHelp('');
      }

      if (!isImagePickerTarget) {
        setIsImagePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isCropModalOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeCropModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCropModalOpen, isApplyingCrop]);

  useEffect(() => {
    if (!isCropModalOpen) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const drag = cropDragStateRef.current;
      const canvas = cropCanvasRef.current;

      if (!drag || !canvas || isApplyingCrop) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }

      const dx = ((event.clientX - drag.startClientX) / rect.width) * 100;
      const dy = ((event.clientY - drag.startClientY) / rect.height) * 100;
      const minSpan = MIN_CROP_SPAN_PERCENT;
      const startLeft = drag.start.left;
      const startTop = drag.start.top;
      const startRight = drag.start.right;
      const startBottom = drag.start.bottom;
      const startWidth = 100 - startLeft - startRight;
      const startHeight = 100 - startTop - startBottom;
      let nextLeft = startLeft;
      let nextTop = startTop;
      let nextRight = startRight;
      let nextBottom = startBottom;

      if (drag.mode === 'move') {
        nextLeft = clampPercent(startLeft + dx, 0, 100 - startWidth);
        nextTop = clampPercent(startTop + dy, 0, 100 - startHeight);
        nextRight = 100 - startWidth - nextLeft;
        nextBottom = 100 - startHeight - nextTop;
      } else {
        if (drag.mode.includes('left')) {
          nextLeft = clampPercent(startLeft + dx, 0, 100 - startRight - minSpan);
        }

        if (drag.mode.includes('right')) {
          nextRight = clampPercent(startRight - dx, 0, 100 - startLeft - minSpan);
        }

        if (drag.mode.includes('top')) {
          nextTop = clampPercent(startTop + dy, 0, 100 - startBottom - minSpan);
        }

        if (drag.mode.includes('bottom')) {
          nextBottom = clampPercent(startBottom - dy, 0, 100 - startTop - minSpan);
        }
      }

      setCropLeftPercent(nextLeft);
      setCropTopPercent(nextTop);
      setCropRightPercent(nextRight);
      setCropBottomPercent(nextBottom);
    };

    const stopDrag = () => {
      cropDragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
    };
  }, [isCropModalOpen, isApplyingCrop]);

  useEffect(() => {
    if (!listRef.current || !messagesEndRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    });
  }, [messages, isLoading]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    const maxHeightPx = rootFontSize * 40; // 40rem
    const baseHeightPx = 52;
    textarea.style.height = 'auto';

    const nextHeight = Math.min(textarea.scrollHeight, maxHeightPx);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
    setIsInputExpanded(nextHeight > baseHeightPx + 2);
  }, [input, pastedImage]);

  const canSend = useMemo(() => {
    return input.trim().length > 0 && !isLoading;
  }, [input, isLoading]);

  const setImageFromFile = async (imageFile) => {
    if (!imageFile) {
      return;
    }

    if (!imageFile.type || !imageFile.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    try {
      const dataUrl = await resizeImageFile(imageFile);
      const parsed = parseDataUrl(dataUrl);

      if (!parsed) {
        setError('이미지 형식을 처리할 수 없습니다.');
        return;
      }

      setError('');
      setPastedImage({
        dataUrl,
        mimeType: parsed.mimeType,
        dataBase64: parsed.dataBase64,
        bytes: parsed.bytes,
      });
    } catch (resizeError) {
      setError(resizeError.message || '이미지 읽기에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const openCropForFile = async (imageFile) => {
    if (!imageFile) {
      return;
    }

    if (!imageFile.type || !imageFile.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    try {
      const sourceDataUrl = await readFileAsDataUrl(imageFile);
      setError('');
      setIsImagePickerOpen(false);
      setCropTopPercent(0);
      setCropBottomPercent(0);
      setCropLeftPercent(0);
      setCropRightPercent(0);
      setCropSourceDataUrl(sourceDataUrl);
      setIsCropModalOpen(true);
    } catch (readError) {
      setError(readError.message || '이미지 읽기에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleApplyCrop = async () => {
    if (!cropSourceDataUrl || isApplyingCrop) {
      return;
    }

    if ((cropTopPercent + cropBottomPercent >= (100 - MIN_CROP_SPAN_PERCENT))
      || (cropLeftPercent + cropRightPercent >= (100 - MIN_CROP_SPAN_PERCENT))) {
      setError('남는 영역이 너무 작습니다. 상/하/좌/우 자르기 값을 줄여 주세요.');
      return;
    }

    try {
      setIsApplyingCrop(true);
      const croppedDataUrl = await cropImageDataUrlByPercentRect(cropSourceDataUrl, {
        top: cropTopPercent,
        bottom: cropBottomPercent,
        left: cropLeftPercent,
        right: cropRightPercent,
      });
      const resizedDataUrl = await resizeImageDataUrl(croppedDataUrl);
      const parsed = parseDataUrl(resizedDataUrl);

      if (!parsed) {
        setError('이미지 형식을 처리할 수 없습니다.');
        return;
      }

      setError('');
      setPastedImage({
        dataUrl: resizedDataUrl,
        mimeType: parsed.mimeType,
        dataBase64: parsed.dataBase64,
        bytes: parsed.bytes,
      });
      setIsCropModalOpen(false);
      setCropSourceDataUrl('');
      setCropTopPercent(0);
      setCropBottomPercent(0);
      setCropLeftPercent(0);
      setCropRightPercent(0);
    } catch (cropError) {
      setError(cropError.message || '이미지 자르기에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const startCropDrag = (mode, event) => {
    if (!isCropModalOpen || isApplyingCrop) {
      return;
    }

    event.preventDefault();

    cropDragStateRef.current = {
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      start: {
        top: cropTopPercent,
        right: cropRightPercent,
        bottom: cropBottomPercent,
        left: cropLeftPercent,
      },
    };
  };

  const sendMessage = async () => {
    const question = input.trim();
    if (isLoading || requestInFlightRef.current) {
      return;
    }

    if (!question) {
      setError('질문을 입력하세요.');
      return;
    }

    const attachedImage = pastedImage;
    requestInFlightRef.current = true;

    const userMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: question,
      imageDataUrl: attachedImage?.dataUrl || '',
      createdAt: Date.now(),
    };

    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');
    setPastedImage(null);
    setIsImagePickerOpen(false);
    setError('');
    setIsLoading(true);

    const compactHistoryText = (text) => {
      if (typeof text !== 'string') {
        return '';
      }

      return text
        .replace(/```[\s\S]*?```/g, '[코드/그래프 생략]')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 1000);
    };

    const lastAssistantIndex = messages.reduce((lastIndex, item, index) => {
      return item.role === 'assistant' ? index : lastIndex;
    }, -1);
    const completedMessages = lastAssistantIndex >= 0 ? messages.slice(0, lastAssistantIndex + 1) : [];

    // 현재 질문은 별도 message 필드로 전달하므로 history에는 이전 대화만 담는다.
    const history = completedMessages
      .filter((item) => item.role === 'user' || item.role === 'assistant')
      .map((item) => ({
        role: item.role,
        content: compactHistoryText(item.imageDataUrl ? `${item.content}\n[이미지 첨부됨]` : item.content),
      }))
      .filter((item) => item.content)
      .slice(-8);

    try {
      let requestBody = { message: question, history, gradeLevel, answerMode };

      if (attachedImage?.dataUrl) {
        const imageBlob = dataUrlToBlob(attachedImage.dataUrl);

        if (!imageBlob) {
          throw new Error('이미지 파일 변환에 실패했습니다.');
        }

        if (isBillingUiEnabled()) {
          // The billing helper asks for consent before sending this multipart request.
          // Keeping the image in that request avoids an orphan S3 upload on cancellation.
          requestBody = new FormData();
          requestBody.append('message', question);
          requestBody.append('history', JSON.stringify(history));
          requestBody.append('gradeLevel', gradeLevel);
          requestBody.append('answerMode', answerMode);
          const extension = imageBlob.type === 'image/png' ? 'png' : imageBlob.type === 'image/webp' ? 'webp' : 'jpg';
          requestBody.append('imageFile', imageBlob, `ai-study-image.${extension}`);
        } else {
          const uploaded = await uploadImageBlobToS3(serverUrl, imageBlob, attachedImage.mimeType);
          requestBody = {
            ...requestBody,
            imageS3Key: uploaded.s3Key,
            imageS3Bucket: uploaded.s3Bucket,
            deleteImageAfterUse: Boolean(uploaded.s3Key),
          };
        }
      }

      const response = await postWithAiBilling('ai_study', `${serverUrl}/aiStudy/ask`, requestBody, {}, {
        quote: { answerMode },
        fingerprintData: { message: question, history, gradeLevel, answerMode, image: attachedImage?.dataUrl || '' },
      });

      const res = response.data;

      if (res?.success && res?.answer) {
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: res.answer,
            createdAt: Date.now(),
          },
        ]);
      } else {
        setError(res?.error || 'AI 응답에 실패했습니다.');
      }
    } catch (requestError) {
      const statusCode = requestError.response?.status;
      const serverError = requestError.response?.data?.error;
      const serverDetail = requestError.response?.data?.detail;
      const fallbackMessage = requestError?.message || '';

      if (requestError.code === 'ERR_CANCELED') {
        setMessages(messages);
        setInput(question);
        setPastedImage(attachedImage);
      } else if (fallbackMessage.includes('S3 업로드 URL 생성에 실패')) {
        setError('S3 업로드 URL 생성에 실패했습니다. 서버 AWS 권한/버킷 설정을 확인해 주세요.');
      } else if (fallbackMessage.includes('브라우저에서 차단')) {
        setError('S3 업로드가 브라우저에서 차단되었습니다. S3 버킷 CORS에 localhost origin을 추가해 주세요.');
      } else if (fallbackMessage.includes('S3 업로드 실패')) {
        setError('S3 파일 업로드가 실패했습니다. 버킷 CORS/권한 설정을 확인해 주세요.');
      } else if (fallbackMessage.includes('이미지 파일 변환에 실패')) {
        setError('이미지 파일 변환에 실패했습니다. 다른 이미지로 다시 시도해 주세요.');
      } else if (statusCode === 413) {
        setError('이미지가 커서 서버가 요청을 처리하지 못했습니다. 두 문제 이미지는 반으로 나눠서 첨부해 주세요.');
      } else {
        setError(serverDetail ? `${serverError || 'AI 호출에 실패했습니다.'} (${serverDetail})` : serverError || fallbackMessage || 'AI 호출에 실패했습니다.');
      }
    } finally {
      requestInFlightRef.current = false;
      setIsLoading(false);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handlePaste = (event) => {
    if (isLoading) {
      return;
    }

    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems || clipboardItems.length === 0) {
      return;
    }

    const imageItem = [...clipboardItems].find((item) => item.type && item.type.startsWith('image/'));
    if (!imageItem) {
      return;
    }

    const imageFile = imageItem.getAsFile();
    if (!imageFile) {
      return;
    }

    event.preventDefault();
    setImageFromFile(imageFile);
  };

  const handleAddImageClick = () => {
    if (isLoading) {
      return;
    }

    setIsImagePickerOpen((prev) => !prev);
  };

  const handleSelectImageClick = () => {
    if (isLoading) {
      return;
    }

    setIsImagePickerOpen(false);
    galleryInputRef.current?.click();
  };

  const handleCaptureImageClick = () => {
    if (isLoading) {
      return;
    }

    setIsImagePickerOpen(false);
    cameraInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    openCropForFile(selectedFile);
    event.target.value = '';
  };

  const handleDownloadTodayPdf = async () => {
    if (!listRef.current || isPdfDownloading || isLoading) {
      return;
    }

    const shouldDownload = window.confirm('오늘 대화 내용을 PDF로 다운로드하시겠습니까?');
    if (!shouldDownload) {
      return;
    }

    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const tomorrowStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).getTime();
    const todayMessageIds = new Set(
      messages
        .filter((message) => {
          const createdAt = Number(message.createdAt);
          return !Number.isFinite(createdAt) || (createdAt >= todayStart && createdAt < tomorrowStart);
        })
        .map((message) => String(message.id)),
    );
    const messageNodes = Array.from(
      listRef.current.querySelectorAll('.ai-message-item:not(.loading)'),
    ).filter((node) => todayMessageIds.has(node.dataset.messageId));

    if (!messageNodes.length) {
      setError('오늘 저장할 대화가 없습니다.');
      return;
    }

    setIsPdfDownloading(true);
    setError('');

    try {
      // PDF 렌더링이 메인 스레드를 점유하기 전에 로딩 UI를 먼저 화면에 반영한다.
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: false });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 11;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      const paintPage = () => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const exportWidth = 800;
      const exportNode = listRef.current.cloneNode(true);
      exportNode.querySelectorAll('.ai-message-item').forEach((node) => {
        if (node.classList.contains('loading') || !todayMessageIds.has(node.dataset.messageId)) {
          node.remove();
        }
      });
      exportNode.querySelectorAll('.ai-scroll-anchor').forEach((node) => node.remove());
      exportNode.classList.add('is-pdf-exporting');
      const exportWrapper = document.createElement('div');
      Object.assign(exportWrapper.style, {
        position: 'fixed',
        left: '0',
        top: '100vh',
        width: `${exportWidth}px`,
        height: '1px',
        overflow: 'hidden',
        pointerEvents: 'none',
      });
      exportWrapper.appendChild(exportNode);
      document.body.appendChild(exportWrapper);

      const exportHeight = Math.max(1, Math.ceil(exportNode.scrollHeight));

      let dataUrl = '';
      try {
        dataUrl = await toJpeg(exportNode, {
          cacheBust: false,
          pixelRatio: 2,
          quality: 0.96,
          backgroundColor: '#ffffff',
          width: exportWidth,
          height: exportHeight,
          style: {
            position: 'static',
            left: 'auto',
            top: 'auto',
            zIndex: 'auto',
            width: `${exportWidth}px`,
            height: `${exportHeight}px`,
            maxHeight: 'none',
            overflow: 'visible',
          },
        });
      } finally {
        exportWrapper.remove();
      }

      const image = await loadPdfImage(dataUrl);
      const pixelsPerMm = image.naturalWidth / contentWidth;
      const maxSliceHeight = Math.max(1, Math.floor(contentHeight * pixelsPerMm));
      let sourceY = 0;

      paintPage();
      while (sourceY < image.naturalHeight) {
        const proposedHeight = Math.min(maxSliceHeight, image.naturalHeight - sourceY);
        const sliceHeight = findPdfSliceHeight(image, sourceY, proposedHeight);
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = sliceHeight;
        const context = canvas.getContext('2d');

        if (!context) {
          throw new Error('PDF 페이지를 만들 수 없습니다.');
        }

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(
          image,
          0,
          sourceY,
          image.naturalWidth,
          sliceHeight,
          0,
          0,
          image.naturalWidth,
          sliceHeight,
        );

        const sliceDataUrl = canvas.toDataURL('image/jpeg', 0.96);
        const sliceRenderedHeight = sliceHeight / pixelsPerMm;
        pdf.addImage(sliceDataUrl, 'JPEG', margin, margin, contentWidth, sliceRenderedHeight, undefined, 'FAST');
        sourceY += sliceHeight;

        if (sourceY < image.naturalHeight) {
          pdf.addPage();
          paintPage();
        }
      }

      pdf.save(`${formatLocalDate(today)}_AI학습_대화.pdf`);
    } catch (pdfError) {
      console.error('PDF download failed:', pdfError);
      setError('PDF를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsPdfDownloading(false);
    }
  };

  return (
    <div className={`ai-study-page ${isPdfDownloading ? 'is-pdf-downloading' : ''}`.trim()}>
      <BillingBalance showWalletLink={false} />
      <button
        type="button"
        className={`ai-study-pdf-btn ${isPdfDownloading ? 'is-loading' : ''}`}
        onClick={handleDownloadTodayPdf}
        disabled={isLoading || isPdfDownloading}
        title="오늘 대화 PDF 저장"
        aria-label="오늘 대화를 PDF로 저장"
      >
        {isPdfDownloading
          ? <FiLoader className="ai-study-pdf-spinner" aria-hidden="true" />
          : <FiDownload aria-hidden="true" />}
        <span>PDF</span>
      </button>
      <button
        type="button"
        className="ai-study-close-btn"
        onClick={() => navigate('/menu')}
        title="닫기"
        aria-label="메뉴로 이동"
      >
        <AiOutlineClose size={16} />
      </button>
      <section className="ai-study-shell">
        <div className="ai-message-list" ref={listRef}>
          {messages.map((message) => (
            <article
              key={message.id}
              data-message-id={message.id}
              className={`ai-message-item ${message.role === 'user' ? 'user' : 'assistant'}`}
            >
              {message.role === 'user' && message.imageDataUrl ? (
                <div className="ai-user-message-stack">
                  <div className="ai-message-image-wrap user-top-image">
                    <img src={message.imageDataUrl} alt="첨부 이미지" className="ai-message-image" />
                  </div>
                  {message.content && (
                    <div className="bubble">
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                        components={{ code: MarkdownCode }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bubble">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{ code: MarkdownCode }}
                  >
                    {message.content}
                  </ReactMarkdown>
                  {message.imageDataUrl && (
                    <div className="ai-message-image-wrap">
                      <img src={message.imageDataUrl} alt="첨부 이미지" className="ai-message-image" />
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}

          {isLoading && (
            <article className="ai-message-item assistant">
              <div className="bubble loading">답변 생성 중</div>
            </article>
          )}

          <div className="ai-scroll-anchor" ref={messagesEndRef} />
        </div>

        {error && <div className="ai-error">{error}</div>}

        <form className="ai-input-wrap" onSubmit={handleSubmit}>
          <div className="ai-grade-filter">
            <div className="ai-grade-level-group" role="radiogroup" aria-label="교과과정 선택">
              <div className="ai-group-help" ref={gradeHelpRef}>
                <button
                  type="button"
                  className="ai-group-tooltip"
                  aria-label="교과 과정 안내"
                  aria-expanded={activeHelp === 'grade'}
                  onClick={() => setActiveHelp((prev) => (prev === 'grade' ? '' : 'grade'))}
                >
                  ?
                </button>
                {activeHelp === 'grade' && (
                  <div className="ai-group-help-popover" role="tooltip">
                    교과 과정: 답변 난이도와 표현 수준을 선택한 학년대에 맞춥니다.
                  </div>
                )}
              </div>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="gradeLevel"
                  value="all"
                  checked={gradeLevel === 'all'}
                  onChange={(event) => setGradeLevel(event.target.value)}
                  disabled={isLoading}
                />
                <span>전체</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="gradeLevel"
                  value="elementary"
                  checked={gradeLevel === 'elementary'}
                  onChange={(event) => setGradeLevel(event.target.value)}
                  disabled={isLoading}
                />
                <span>초등</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="gradeLevel"
                  value="middle"
                  checked={gradeLevel === 'middle'}
                  onChange={(event) => setGradeLevel(event.target.value)}
                  disabled={isLoading}
                />
                <span>중등</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="gradeLevel"
                  value="high"
                  checked={gradeLevel === 'high'}
                  onChange={(event) => setGradeLevel(event.target.value)}
                  disabled={isLoading}
                />
                <span>고등</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="gradeLevel"
                  value="college"
                  checked={gradeLevel === 'college'}
                  onChange={(event) => setGradeLevel(event.target.value)}
                  disabled={isLoading}
                />
                <span>대학</span>
              </label>
            </div>
            <div className="ai-answer-mode-group" role="radiogroup" aria-label="추론 수준">
              <div className="ai-group-help" ref={answerModeHelpRef}>
                <button
                  type="button"
                  className="ai-group-tooltip"
                  aria-label="추론 수준 안내"
                  aria-expanded={activeHelp === 'answer'}
                  onClick={() => setActiveHelp((prev) => (prev === 'answer' ? '' : 'answer'))}
                >
                  ?
                </button>
                {activeHelp === 'answer' && (
                  <div className="ai-group-help-popover" role="tooltip">
                    추론 수준: 즉시(빠름), 중간(균형), 높음(정밀) 중 답변 방식과 깊이를 조절합니다.
                  </div>
                )}
              </div>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="answerMode"
                  value="fast"
                  checked={answerMode === 'fast'}
                  onChange={(event) => setAnswerMode(event.target.value)}
                  disabled={isLoading}
                />
                <span>즉시</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="answerMode"
                  value="normal"
                  checked={answerMode === 'normal'}
                  onChange={(event) => setAnswerMode(event.target.value)}
                  disabled={isLoading}
                />
                <span>중간</span>
              </label>
              <label className="ai-grade-option">
                <input
                  type="radio"
                  name="answerMode"
                  value="precise"
                  checked={answerMode === 'precise'}
                  onChange={(event) => setAnswerMode(event.target.value)}
                  disabled={isLoading}
                />
                <span>높음</span>
              </label>
            </div>
          </div>
          <div className={`ai-input-field ${(pastedImage || isInputExpanded) ? 'is-expanded' : ''}`.trim()}>
            <div className="ai-input-image-picker" ref={imagePickerRef}>
              <button
                type="button"
                className="ai-input-add-image"
                onClick={handleAddImageClick}
                disabled={isLoading}
                aria-label="이미지 추가"
                aria-haspopup="menu"
                aria-expanded={isImagePickerOpen}
                title="이미지 추가"
              >
                <img src={addImageIcon} alt="" aria-hidden="true" />
              </button>
              <input
                ref={galleryInputRef}
                className="ai-input-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isLoading}
              />
              <input
                ref={cameraInputRef}
                className="ai-input-file-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                disabled={isLoading}
              />
              {isImagePickerOpen && (
                <div className="ai-image-source-menu" role="menu" aria-label="이미지 추가 방식 선택">
                  <button
                    type="button"
                    className="ai-image-source-option"
                    role="menuitem"
                    onClick={handleSelectImageClick}
                    disabled={isLoading}
                  >
                    이미지 선택
                  </button>
                  <button
                    type="button"
                    className="ai-image-source-option"
                    role="menuitem"
                    onClick={handleCaptureImageClick}
                    disabled={isLoading}
                  >
                    사진 촬영
                  </button>
                </div>
              )}
            </div>
            {pastedImage && (
              <div className="ai-input-image-preview" aria-label="붙여넣은 이미지 미리보기">
                <img src={pastedImage.dataUrl} alt="붙여넣은 이미지" className="ai-input-image-preview-img" />
                <button
                  type="button"
                  className="ai-input-image-remove"
                  onClick={() => setPastedImage(null)}
                  disabled={isLoading}
                  aria-label="이미지 제거"
                  title="이미지 제거"
                >
                  <img src={removeIcon} alt="" aria-hidden="true" />
                </button>
              </div>
            )}
            <textarea
              ref={textareaRef}
              className={`${isInputExpanded ? 'is-expanded' : ''} ${pastedImage ? 'has-image' : ''}`.trim()}
              placeholder="질문을 입력하세요"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="ai-input-send"
              disabled={!canSend}
              aria-label="전송"
              title="전송"
            >
              <img src={uploadIcon} alt="" aria-hidden="true" />
            </button>
          </div>
        </form>

        {isCropModalOpen && (
          <div
            className="ai-crop-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label="이미지 자르기"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeCropModal();
              }
            }}
          >
            <div className="ai-crop-modal" onClick={(event) => event.stopPropagation()}>
              <h3 className="ai-crop-title">사진 영역 자르기</h3>
              <div className="ai-crop-preview-wrap" aria-live="polite">
                <div className="ai-crop-preview-frame" ref={cropCanvasRef}>
                  <img src={cropSourceDataUrl} alt="자르기 미리보기" className="ai-crop-preview-image" />
                  <div className="ai-crop-mask ai-crop-mask-top" style={{ height: `${cropTopPercent}%` }} />
                  <div className="ai-crop-mask ai-crop-mask-bottom" style={{ height: `${cropBottomPercent}%` }} />
                  <div
                    className="ai-crop-mask ai-crop-mask-left"
                    style={{
                      top: `${cropTopPercent}%`,
                      bottom: `${cropBottomPercent}%`,
                      width: `${cropLeftPercent}%`,
                    }}
                  />
                  <div
                    className="ai-crop-mask ai-crop-mask-right"
                    style={{
                      top: `${cropTopPercent}%`,
                      bottom: `${cropBottomPercent}%`,
                      width: `${cropRightPercent}%`,
                    }}
                  />
                  <button
                    type="button"
                    className="ai-crop-focus-window"
                    style={{
                      top: `${cropTopPercent}%`,
                      bottom: `${cropBottomPercent}%`,
                      left: `${cropLeftPercent}%`,
                      right: `${cropRightPercent}%`,
                    }}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="ai-crop-move-cross"
                    style={{
                      left: `${(100 - cropLeftPercent - cropRightPercent) / 2 + cropLeftPercent}%`,
                      top: `${(100 - cropTopPercent - cropBottomPercent) / 2 + cropTopPercent}%`,
                    }}
                    onPointerDown={(event) => startCropDrag('move', event)}
                    aria-label="선택 영역 이동"
                  >
                    <FiPlus size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ai-crop-handle top"
                    style={{ top: `${cropTopPercent}%`, left: `${(100 - cropLeftPercent - cropRightPercent) / 2 + cropLeftPercent}%` }}
                    onPointerDown={(event) => startCropDrag('top', event)}
                    aria-label="위쪽 경계 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle bottom"
                    style={{ bottom: `${cropBottomPercent}%`, left: `${(100 - cropLeftPercent - cropRightPercent) / 2 + cropLeftPercent}%` }}
                    onPointerDown={(event) => startCropDrag('bottom', event)}
                    aria-label="아래쪽 경계 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle left"
                    style={{ left: `${cropLeftPercent}%`, top: `${(100 - cropTopPercent - cropBottomPercent) / 2 + cropTopPercent}%` }}
                    onPointerDown={(event) => startCropDrag('left', event)}
                    aria-label="왼쪽 경계 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle right"
                    style={{ right: `${cropRightPercent}%`, top: `${(100 - cropTopPercent - cropBottomPercent) / 2 + cropTopPercent}%` }}
                    onPointerDown={(event) => startCropDrag('right', event)}
                    aria-label="오른쪽 경계 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle top-left"
                    style={{ top: `${cropTopPercent}%`, left: `${cropLeftPercent}%` }}
                    onPointerDown={(event) => startCropDrag('top-left', event)}
                    aria-label="왼쪽 위 모서리 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle top-right"
                    style={{ top: `${cropTopPercent}%`, right: `${cropRightPercent}%` }}
                    onPointerDown={(event) => startCropDrag('top-right', event)}
                    aria-label="오른쪽 위 모서리 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle bottom-left"
                    style={{ bottom: `${cropBottomPercent}%`, left: `${cropLeftPercent}%` }}
                    onPointerDown={(event) => startCropDrag('bottom-left', event)}
                    aria-label="왼쪽 아래 모서리 조절"
                  />
                  <button
                    type="button"
                    className="ai-crop-handle bottom-right"
                    style={{ bottom: `${cropBottomPercent}%`, right: `${cropRightPercent}%` }}
                    onPointerDown={(event) => startCropDrag('bottom-right', event)}
                    aria-label="오른쪽 아래 모서리 조절"
                  />
                </div>
              </div>
              <div className="ai-crop-actions">
                <button
                  type="button"
                  className="ai-crop-btn cancel"
                  onClick={closeCropModal}
                  disabled={isApplyingCrop}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="ai-crop-btn apply"
                  onClick={handleApplyCrop}
                  disabled={isApplyingCrop}
                >
                  {isApplyingCrop ? '처리 중...' : '적용 후 첨부'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default AiStudy;
